import React from "react";
import type {SponsorOptions} from "../sponsorBooking";
import {PosterCanvas} from "./PosterCanvas";
import {PosterAccentLine} from "./SponsorPoster.styles";
import {OWNER_THEME_POSTER_TESTID} from "./takeoverTestIds";

/**
 * The SENDER's own words on their wallpaper page theme (kitecorp/transferify-ui#1869).
 *
 * The counterpart of {@link SponsorPoster}, and deliberately a much smaller thing. A paid wallpaper
 * buys a poster: an ad label, a headline, the advertiser's accent line, a third line and a
 * click-through. A page theme buys none of that, because the api's page-theme contract carries none of
 * it — no headline column, no CTA, no logo asset, no advertiser to name. What a sender can set is the
 * five `options` keys, and exactly one of them is text. So the whole of this poster is that one line,
 * laid out at the placement they chose, in the colour and on the background they chose.
 *
 * ## Why the line is the ACCENT line rather than the headline
 *
 * Because it is the only line, and the accent line is the display size a poster's second statement is
 * set at. Its colour is no longer what decides this: since kitecorp/transferify-ui#2416 every word on a
 * poster takes the chosen text colour (`posterComposition`), so a sender who picks a colour sees it and
 * one who picks none gets words that read against their background or their picture.
 *
 * ## What it must never grow
 *
 * The ad label. There is no advertisement on a sender's own page, so there is nothing to disclose and
 * nothing to link to — and `download/pageBackdrop.ts`'s `ownerPosterLine` is what makes that structural
 * rather than remembered: it returns `null` for every source except a winning owner theme, so an
 * advertiser's copy can never reach this component and be typeset without its label.
 *
 * Lives beside the sponsor poster rather than under `download/` for the reason `SponsorTakeoverArt`
 * does: this folder is where the wallpaper stage is implemented, and both mounts of it — the paid one
 * and the owner's — have to keep reading one set of layout rules.
 */

export interface OwnerThemePosterProps {
	/** The sender's line. Never blank: `ownerPosterLine` is what decides there is a poster at all. */
	line: string;
	/** The settings the layout is composed from, or absent for the defaults. */
	options: SponsorOptions | null | undefined;
	/** The pool the wallpaper cycles, so the contrast scrim measures the grounds the line sits on. */
	imageUrls: readonly string[];
	/** The element the photograph fills. See {@link PosterCanvas}'s own note on why it must be that box. */
	artLayer: HTMLElement | null;
	/** Laid over the picture at every width — see `PosterCanvasProps.composed`. The page leaves it unset. */
	composed?: boolean;
	/**
	 * The block's `data-testid`. Defaults to the recipient page's own, so a second mount — the builder's
	 * miniature — has to name itself: the two must be individually addressable for a spec to prove the
	 * line reached the MINIATURE rather than some page.
	 */
	testId?: string;
}

export function OwnerThemePoster(
	{line, options, imageUrls, artLayer, composed, testId = OWNER_THEME_POSTER_TESTID}: OwnerThemePosterProps,
): React.ReactElement {
	return (
		<PosterCanvas
			options={options}
			imageUrls={imageUrls}
			artLayer={artLayer}
			composed={composed}
			testId={testId}
			/*
			 * NOT a landmark. An advertisement is somebody else's content on this page and a reader is
			 * entitled to find it, skip it and be told whose it is; a sender's own line is this page's own
			 * words, like the note on the download card. Naming it would announce a region boundary and
			 * then have nothing useful to say about it.
			 */
			ariaLabel={null}
			/* The page-theme contract stores no brand mark, so there is never one to lay in the corner. */
			logo={null}
		>
			<PosterAccentLine>{line}</PosterAccentLine>
		</PosterCanvas>
	);
}
